/**
 * Tenant Invitations tab (saas_auth spec 0011). Create / list / revoke only —
 * mirrors the Members "Add member" dialog + table conventions (TenantMembers):
 * tenant read via useRouteLoaderData('tenant-workspace'), invitations fetched
 * in-component (useEffect + refresh()), MUI Dialog + react-hook-form invite,
 * StatusChip, ConfirmDialog, useSnackbar.
 *
 * Spec 0058 (saas-auth SASATH-0076) — U4. The old global `oidcSsoEnabled`
 * boolean (form checkbox + list chip) is gone (neutralized in U1); it is replaced
 * by an explicit OIDC-client multi-select. The invite form picks 0/1/many OIDC
 * clients (value = `clientDbId`) from `tenants.oidcClientsForTenant(tenantId)` —
 * the clients whose allow-list includes this tenant — and submits them as
 * `oidcClientIds`. Empty is normal (the member joins with no OIDC access). The
 * list resolves each invitation's granted `oidcClientIds` to client names via the
 * same picker. See the delivery's Architect notes for the pending-0076 contract.
 *
 * Non-Goals honored: no accept screen (saas_login), no inviteUrl surfaced, no
 * resend, no SUPER_ADMIN role option, no "Invited by" column.
 *
 * SASADM-0169 (saas-auth SASATH-0383) — the role picker is grouped Employee app / Admin app
 * (shared `RoleMenuItems`, adds Staff); the email field shows the tenant's Microsoft domain
 * hint (addresses on it are pre-authorized: no email); the success snackbar follows
 * `deliveryMode`; pending pre-authorized rows carry a chip. 0383 fields render when present.
 */
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useMemo, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  OidcClientPickerDto,
  SendInvitationRequest,
  SendInvitationResponse,
  TenantInvitationDto,
  TenantUserRole,
} from '@/api/types';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { StatusChip } from '@/components/StatusChip';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { RoleMenuItems } from '@/components/RolePicker';
import type { TenantWorkspaceLoaderData } from './TenantWorkspace';

type FilterValue = 'ALL' | 'PENDING' | 'ACCEPTED';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

// Resolve an invitation's granted client DB ids to display names via the picker
// (clientDbId → clientName). An id not in the picker (e.g. a client that since
// dropped this tenant from its allow-list) falls back to the raw id so the row
// still renders. Absent/empty `oidcClientIds` → [] → "No OIDC access" (this is
// also how a migrated old invite with no SSO renders — see the delivery notes).
function resolveClientNames(
  ids: string[] | undefined,
  nameById: Map<string, string>,
): string[] {
  if (!ids || ids.length === 0) return [];
  return ids.map((id) => nameById.get(id) ?? id);
}

export function TenantInvitations() {
  const data = useRouteLoaderData('tenant-workspace') as TenantWorkspaceLoaderData;
  const tenant = data.tenant;
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<TenantInvitationDto[] | null>(null);
  // The OIDC-client picker for this tenant — drives the invite multi-select and
  // resolves the list's granted-client names. `null` until loaded / on a failed
  // read (display-only resilience: the list shows raw ids, the form shows the
  // not-allow-listed hint). The picker is tenant-scoped and small.
  const [clients, setClients] = useState<OidcClientPickerDto[] | null>(null);
  const [openInvite, setOpenInvite] = useState(false);
  const [filter, setFilter] = useState<FilterValue>('ALL');
  const [confirm, setConfirm] = useState<{
    title: string;
    body: string;
    onConfirm: () => Promise<void>;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  // SASADM-0169 — the tenant's Microsoft domain hint (non-secret `entra-sso.domain_hint`),
  // read tolerantly: any error (including 403 for a non-super-admin) → no hint.
  const [domainHint, setDomainHint] = useState('');

  async function refresh() {
    try {
      const list = await apiAdmin.invitations.list(tenant.id);
      // Newest first (backend already orders this way; sort defensively).
      list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setRows(list);
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setRows([]);
    }
  }

  useEffect(() => {
    // `refresh()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await refresh();
    })();
    // The OIDC-client picker (display-only — a failed read degrades to an empty
    // list: no crash, the form shows the not-allow-listed hint).
    void apiAdmin.tenants
      .oidcClientsForTenant(tenant.id)
      .then(setClients)
      .catch(() => setClients([]));
    void apiAdmin.integrationSettings
      .listGroups(tenant.id)
      .then((groups) => {
        const hint = groups
          .find((g) => g.group === 'entra-sso')
          ?.keys.find((k) => k.key === 'domain_hint');
        setDomainHint(hint && !hint.isSecret ? hint.value.trim() : '');
      })
      .catch(() => setDomainHint(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    if (filter === 'ALL') return rows;
    return rows.filter((r) => r.status === filter);
  }, [rows, filter]);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clients ?? []) m.set(c.clientDbId, c.clientName);
    return m;
  }, [clients]);

  async function revoke(inv: TenantInvitationDto) {
    setBusy(true);
    try {
      await apiAdmin.invitations.revoke(tenant.id, inv.id);
      snack.success(t('snackbar.invitationRevoked', 'Invitation revoked.'));
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVITATION_ALREADY_ACCEPTED') {
        snack.error(
          t('errors.codes.INVITATION_ALREADY_ACCEPTED', 'This invitation was already accepted.'),
        );
        await refresh();
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      } else {
        snack.error(t('errors.generic', 'Something went wrong.'));
      }
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h2" component="h2">
          {t('invitations.title', 'Invitations')}
        </Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenInvite(true)}>
          {t('invitations.invite', 'Invite')}
        </Button>
      </Stack>
      <Tabs value={filter} onChange={(_e, v: FilterValue) => setFilter(v)} sx={{ mb: 2 }}>
        <Tab value="ALL" label={t('invitations.filter.all', 'All')} />
        <Tab value="PENDING" label={t('invitations.filter.pending', 'Pending')} />
        <Tab value="ACCEPTED" label={t('invitations.filter.accepted', 'Accepted')} />
      </Tabs>
      {filtered === null ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('common.loading', 'Loading')}
          </Typography>
        </Paper>
      ) : filtered.length === 0 ? (
        <Alert severity="info">{t('invitations.empty', 'No invitations yet.')}</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('invitations.columns.email', 'Email')}</TableCell>
                <TableCell>{t('invitations.columns.role', 'Role')}</TableCell>
                <TableCell>{t('invitations.columns.oidcClients', 'OIDC access')}</TableCell>
                <TableCell>{t('invitations.columns.status', 'Status')}</TableCell>
                <TableCell>{t('invitations.columns.invited', 'Invited')}</TableCell>
                <TableCell>{t('invitations.columns.expires', 'Expires')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((inv) => {
                const grantedNames = resolveClientNames(inv.oidcClientIds, nameById);
                return (
                  <TableRow key={inv.id}>
                    <TableCell>{inv.email}</TableCell>
                    <TableCell>
                      {inv.role === 'USER'
                        ? t('members.roles.EMPLOYEE', 'Employee')
                        : t(`members.roles.${inv.role}`, inv.role)}
                    </TableCell>
                    <TableCell>
                      {grantedNames.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('invitations.noOidcAccess', 'No OIDC access')}
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                          {grantedNames.map((name) => (
                            <Chip key={name} label={name} size="small" />
                          ))}
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <StatusChip kind="invitationStatus" value={inv.status} />
                        {inv.status === 'PENDING' && inv.preAuthorized === true ? (
                          <Tooltip
                            title={t(
                              'invitations.preAuthorizedTooltip',
                              'Activates on first Microsoft sign-in · expires 30 days after creation',
                            )}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('invitations.preAuthorized', 'Pre-authorized')}
                            />
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{fmtDate(inv.createdAt)}</TableCell>
                    <TableCell>{fmtDate(inv.expiresAt)}</TableCell>
                    <TableCell align="right">
                      {inv.status === 'PENDING' ? (
                        <Button
                          size="small"
                          color="error"
                          onClick={() =>
                            setConfirm({
                              title: t('invitations.confirm.revokeTitle', 'Revoke invitation?'),
                              body: t(
                                'invitations.confirm.revokeBody',
                                'Revoke the invitation for {{email}}? The invite link will stop working.',
                                { email: inv.email },
                              ),
                              onConfirm: () => revoke(inv),
                            })
                          }
                        >
                          {t('invitations.actions.revoke', 'Revoke')}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <InviteMemberDialog
        open={openInvite}
        tenantId={tenant.id}
        clients={clients ?? []}
        domainHint={domainHint}
        onClose={() => setOpenInvite(false)}
        onSent={(email, res) => {
          setOpenInvite(false);
          void refresh();
          if (res.deliveryMode === 'PRE_AUTHORIZED') {
            snack.success(
              t(
                'invitations.sentPreAuthorized',
                'Pre-authorized. {{email}} gets access on their first Microsoft sign-in. No email was sent.',
                { email },
              ),
            );
          } else {
            snack.success(t('snackbar.invitationSent', 'Invitation sent to {{email}}.', { email }));
          }
        }}
      />
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        severity="error"
        confirmLabel={t('invitations.actions.revoke', 'Revoke')}
        busy={busy}
        onConfirm={() => void confirm?.onConfirm()}
        onCancel={() => setConfirm(null)}
      />
    </Box>
  );
}

interface InviteMemberDialogProps {
  open: boolean;
  tenantId: number;
  // The OIDC clients whose allow-list includes this tenant (the picker). Empty
  // when the tenant is on no client's allow-list → the field shows a hint, not an
  // error. Values are `clientDbId`.
  clients: OidcClientPickerDto[];
  // The tenant's Microsoft domain hint, trimmed; '' → no hint line.
  domainHint: string;
  onClose: () => void;
  onSent: (email: string, res: SendInvitationResponse) => void;
}

interface InviteFormValues {
  email: string;
  role: TenantUserRole;
  // Chosen OIDC clients to grant on accept (0/1/many) — `clientDbId` values.
  oidcClientIds: string[];
}

function InviteMemberDialog({
  open,
  tenantId,
  clients,
  domainHint,
  onClose,
  onSent,
}: InviteMemberDialogProps) {
  const { t } = useCopy();
  const [error, setError] = useState<ApiError | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const [pending, setPending] = useState(false);
  const form = useForm<InviteFormValues>({
    defaultValues: { email: '', role: 'USER', oidcClientIds: [] },
  });
  const roleItems = RoleMenuItems();

  // Reset the form fields when the dialog opens. `form.reset` targets the
  // react-hook-form store (not React state), so it stays in the effect.
  useEffect(() => {
    if (open) {
      form.reset({ email: '', role: 'USER', oidcClientIds: [] });
    }
  }, [open, form]);

  // Clear the inline error on the open transition via a render-phase state
  // adjustment (React's "adjust state while rendering" pattern) instead of a
  // synchronous setState in an effect — same result, no inter-render error flash.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setError(null);
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setPending(true);
    setError(null);
    const email = values.email.trim();
    // Spec 0058 (SASATH-0076) — the global `oidcSsoEnabled` flag is replaced by
    // the explicit OIDC-client list: submit the chosen `clientDbId`s as
    // `oidcClientIds`. Empty is normal → omit the field (the member joins with no
    // OIDC access).
    const payload: SendInvitationRequest = {
      email,
      role: values.role,
    };
    if (values.oidcClientIds.length > 0) {
      payload.oidcClientIds = values.oidcClientIds;
    }
    try {
      const res = await apiAdmin.invitations.send(tenantId, payload);
      onSent(email, res);
    } catch (err) {
      if (err instanceof ApiError) {
        // Conflicts that are about the email get surfaced as a field error
        // (mirrors the Members USER_NOT_FOUND pattern); other errors → banner.
        if (err.code === 'ALREADY_ACTIVE_MEMBER') {
          form.setError('email', {
            message: t(
              'errors.codes.ALREADY_ACTIVE_MEMBER',
              '{{email}} is already a member of this tenant.',
              {
                email,
              },
            ),
          });
        } else if (err.code === 'INVITATION_ALREADY_PENDING') {
          form.setError('email', {
            message: t(
              'errors.codes.INVITATION_ALREADY_PENDING',
              'An invitation is already pending for {{email}}.',
              { email },
            ),
          });
        } else if (err.code === 'OIDC_CLIENT_NOT_ALLOWED_FOR_TENANT') {
          // Field-level: a chosen client no longer allow-lists this tenant.
          // Shouldn't occur given the pre-filtered picker, but mapped per spec.
          form.setError('oidcClientIds', {
            message: t(
              'invitations.errors.OIDC_CLIENT_NOT_ALLOWED_FOR_TENANT',
              'One or more selected OIDC clients no longer allow-list this tenant. Reopen and try again.',
            ),
          });
        } else {
          setError(err);
        }
      }
    } finally {
      setPending(false);
    }
  });

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('invitations.dialogTitle', 'Invite a person')}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={error} />
        <Box component="form" onSubmit={onSubmit} id="invite-member-form" noValidate>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('invitations.email', 'Email')}
              type="email"
              required
              {...form.register('email', { required: true })}
              error={Boolean(form.formState.errors.email)}
              helperText={
                form.formState.errors.email?.message ??
                (domainHint
                  ? t(
                      'invitations.hintLine',
                      'Addresses on {{domain}} are pre-authorized (no email). Others receive an invitation email.',
                      { domain: domainHint },
                    )
                  : undefined)
              }
            />
            <Controller
              control={form.control}
              name="role"
              render={({ field }) => (
                <TextField {...field} select fullWidth label={t('invitations.role', 'Role')}>
                  {roleItems}
                </TextField>
              )}
            />
            <Controller
              control={form.control}
              name="oidcClientIds"
              render={({ field }) => (
                <FormControl
                  component="fieldset"
                  error={Boolean(form.formState.errors.oidcClientIds)}
                >
                  <FormLabel component="legend">
                    {t('invitations.oidcClients', 'OIDC access')}
                  </FormLabel>
                  {clients.length === 0 ? (
                    <FormHelperText>
                      {t(
                        'invitations.noOidcClients',
                        "This tenant isn't allow-listed on any OIDC client.",
                      )}
                    </FormHelperText>
                  ) : (
                    <>
                      <FormHelperText sx={{ mx: 0 }}>
                        {t(
                          'invitations.oidcClientsHelper',
                          'Grant the new member SSO access to these OIDC clients on accept. Optional — leave all unchecked for no OIDC access.',
                        )}
                      </FormHelperText>
                      <FormGroup>
                        {clients.map((c) => (
                          <FormControlLabel
                            key={c.clientDbId}
                            control={
                              <Checkbox
                                checked={field.value.includes(c.clientDbId)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  field.onChange(
                                    checked
                                      ? [...field.value, c.clientDbId]
                                      : field.value.filter((x) => x !== c.clientDbId),
                                  );
                                }}
                              />
                            }
                            label={c.clientName}
                          />
                        ))}
                      </FormGroup>
                    </>
                  )}
                  {form.formState.errors.oidcClientIds ? (
                    <FormHelperText error>
                      {form.formState.errors.oidcClientIds.message}
                    </FormHelperText>
                  ) : null}
                </FormControl>
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button form="invite-member-form" type="submit" variant="contained" disabled={pending}>
          {t('invitations.invite', 'Invite')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
